/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { ID } from '@gh/common-shared-data';
import { combineLatest$ } from '@gh/rx';
import { distinctUntilChanged$, map$, switchMap$ } from '@gh/rx/operators';
import { Observable } from 'rxjs';
import { GraphicalTakeOffFormState } from '../../graphical-take-off-form.service';
import {
  getGraphicalTakeOffMeasurementId, getGraphicalTakeOffTopType,
  getGraphicalTakeOffTopViewId,
  GraphicalTakeOffDataBoxVisibility,
  GraphicalTakeOffMeasurement,
  GraphicalTakeOffTopView, GraphicalTakeOffViewTransform,
} from '../../resources';

@Component({
  standalone: false,
  selector: 'gh-graphical-take-off-canvas-top',
  templateUrl: 'graphical-take-off-canvas-top.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GraphicalTakeOffCanvasTopComponent implements OnInit {
  @Input() top: GraphicalTakeOffTopView;
  @Input() visible: boolean;
  @Input() viewTransform: GraphicalTakeOffViewTransform;

  type: string;

  highlightedDataBoxId$: Observable<Maybe<ID>>;
  selectedMeasurementId$: Observable<Maybe<ID>>;
  isDataBoxVisible$: Observable<boolean>;
  isSelectedMeasurementOnly$: Observable<boolean>;

  constructor(private form: GraphicalTakeOffFormState) {

  }

  ngOnInit(): void {
    const targetTopId = getGraphicalTakeOffTopViewId(this.top);

    this.type = getGraphicalTakeOffTopType(this.top);

    this.highlightedDataBoxId$ = this.form.highlightedTop$.pipe(
      switchMap$((topId) => targetTopId === topId ? this.form.highlightedDataBox$ : [void 0]),
      distinctUntilChanged$());

    this.selectedMeasurementId$ = this.form.selectedTop$.pipe(
      switchMap$((topId) => targetTopId === topId ? this.form.selectedMeasurement$ : [void 0]),
      distinctUntilChanged$());

    this.isDataBoxVisible$ = combineLatest$(
      this.form.dataBoxVisibility$,
      this.form.currentTopId$).pipe(
        map$(([visibility, currentTopId]) => {
          switch (visibility) {
            case GraphicalTakeOffDataBoxVisibility.ShowAll:
              return true;
            case GraphicalTakeOffDataBoxVisibility.ShowNoDataboxes:
              return false;
            case GraphicalTakeOffDataBoxVisibility.ShowTopOnly:
            case GraphicalTakeOffDataBoxVisibility.ShowSelection:
              return getGraphicalTakeOffTopViewId(this.top) === currentTopId;
            default:
              return false;
          }
        }),
        distinctUntilChanged$());

    this.isSelectedMeasurementOnly$ = this.form.dataBoxVisibility$
      .pipe(map$((visibility) => visibility === GraphicalTakeOffDataBoxVisibility.ShowSelection));
  }

  measurementTrackBy(_: number, measure: GraphicalTakeOffMeasurement): ID {
    return getGraphicalTakeOffMeasurementId(measure);
  }
}
